import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  getBannerScheduleState,
  type BannerScheduleState,
  type HeroBanner,
} from '@/lib/heroBanners';

interface BannerListProps {
  banners: HeroBanner[];
  onReorder: (orderedIds: number[]) => void;
  onEdit: (banner: HeroBanner) => void;
  onDelete: (banner: HeroBanner) => void;
}

const SCHEDULE_BADGES: Record<BannerScheduleState, { label: string; icon: string; className: string }> = {
  none: { label: '', icon: '', className: '' },
  scheduled: { label: 'Programmée', icon: 'ri-time-line', className: 'bg-secondary-100 text-secondary-900' },
  live: { label: 'En promo', icon: 'ri-flashlight-fill', className: 'bg-accent-100 text-accent-700' },
  expired: { label: 'Expirée', icon: 'ri-history-line', className: 'bg-background-200/70 text-foreground-500' },
};

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function scheduleLabel(banner: HeroBanner): string {
  if (!banner.startDate && !banner.endDate) return '';
  const from = banner.startDate ? formatDate(banner.startDate) : '—';
  const to = banner.endDate ? formatDate(banner.endDate) : '—';
  return `Du ${from} au ${to}`;
}

function SortableBannerCard({
  banner,
  index,
  onEdit,
  onDelete,
}: {
  banner: HeroBanner;
  index: number;
  onEdit: (banner: HeroBanner) => void;
  onDelete: (banner: HeroBanner) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: banner.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : undefined,
  };

  const schedule = getBannerScheduleState(banner);
  const badge = SCHEDULE_BADGES[schedule];
  const range = scheduleLabel(banner);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-background-50 border rounded-lg p-3 flex gap-3 ${
        isDragging ? 'border-primary-400' : 'border-background-200/70'
      }`}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        title="Glisser pour réordonner"
        aria-label="Glisser pour réordonner"
        className="flex-shrink-0 w-7 h-20 flex items-center justify-center rounded-md text-foreground-300 hover:text-foreground-500 hover:bg-background-100 cursor-grab active:cursor-grabbing transition-colors"
      >
        <i className="ri-draggable text-lg"></i>
      </button>

      <div className="w-24 h-20 rounded-md overflow-hidden bg-background-100 flex-shrink-0 flex items-center justify-center">
        {banner.image ? (
          <img src={banner.image} alt={banner.title || 'Bannière'} className="w-full h-full object-cover" />
        ) : (
          <i className="ri-image-line text-xl text-foreground-300"></i>
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-semibold text-foreground-400">#{index + 1}</span>
          <span
            className="text-[9px] font-mono text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded-full whitespace-nowrap"
            title='Utilise cet id dans {{hero_banners banner="ID"}}'
          >
            id {banner.id}
          </span>
          <h3 className="text-sm font-semibold text-foreground-900 truncate">
            {banner.title || 'Bannière sans titre'}
          </h3>
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
              banner.active ? 'bg-accent-100 text-accent-700' : 'bg-background-200/70 text-foreground-500'
            }`}
          >
            {banner.active ? 'Active' : 'Masquée'}
          </span>
          {badge.label && (
            <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${badge.className}`}>
              <i className={badge.icon}></i>
              {badge.label}
            </span>
          )}
        </div>

        {banner.subtitle && (
          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{banner.subtitle}</p>
        )}

        {range && (
          <p className="text-[11px] text-foreground-400 mt-1 flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {range}
          </p>
        )}

        <div className="flex items-center gap-1.5 mt-3 flex-wrap">
          <button
            onClick={() => onEdit(banner)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-edit-line"></i>Modifier
          </button>
          <button
            onClick={() => onDelete(banner)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-foreground-500 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Liste des bannières réordonnable par glisser-déposer (dnd-kit).
 */
export default function BannerList({ banners, onReorder, onEdit, onDelete }: BannerListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = banners.findIndex((b) => b.id === active.id);
    const newIndex = banners.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(banners, oldIndex, newIndex);
    onReorder(next.map((b) => b.id));
  };

  if (banners.length <= 1) {
    return (
      <div className="space-y-3">
        {banners.map((banner, index) => (
          <SortableBannerCard key={banner.id} banner={banner} index={index} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={banners.map((b) => b.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3">
          {banners.map((banner, index) => (
            <SortableBannerCard key={banner.id} banner={banner} index={index} onEdit={onEdit} onDelete={onDelete} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}